import { observer } from 'mobx-react-lite';
import { useStore } from '../../../stores/store';
import BottomSidebar from '../../../common/sidebar-bottom/bottom-sidebar.component';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import Button from '../../../common/button/button.component';
import { useRegionSettings } from './use-region-settings-hook';
import { RegionSettingsTitle } from './header-settings-popup.styles';
import InputSearchWithSidebar from '../../../common/input-search-with-sidebar/input-search-with-sidebar.component';
import { CountriesResponse } from '../../../models/countriesModel';
import { ReactComponent as ArrowIcon } from '../../../assets/arrow-right-icon.svg';
import {
  ServicesSubSectionContainer,
  ServicesSubSectionHeader,
} from '../../services/services-subsection/services-subsection.styles';
import { ParcelDetailsSearchInputsContainer } from '../../../routes/parcel-details/parcel-details.styles';
import { Measure } from '../../../utils/parcel-measurements.ts/parcel-measurements-utils';

const SIDEBAR_NAME = 'header_settings_popup_mobile';

const HeaderSettingsPopupMobile = () => {
  const {
    navStore: { toggleSidebarByName },
  } = useStore();

  const {
    t,
    country,
    language,
    currency,
    measures,
    setLanguage,
    setCurrency,
    setMeasures,
    handleCountryChange,
    handleSave,
    countryItems,
    languageItems,
    currencyItems,
    isSaving,
    isGuest,
    getCountryLocalizedTitle,
  } = useRegionSettings();

  return (
    <BottomSidebar
      name={SIDEBAR_NAME}
      height='100%'
      mobPadding='1.6rem'
      backgroundColor='#F4F7FC'
      withBlurredBackground
      onClose={() => {}}>
      <FlexContainer
        $direction='column'
        $gap='1.2rem'
        $align='flex-start'>
        <RegionSettingsTitle>{t('region_settings')}</RegionSettingsTitle>

        <ServicesSubSectionContainer>
          <ServicesSubSectionHeader>
            {t('settings_location_country')}
          </ServicesSubSectionHeader>
          <ParcelDetailsSearchInputsContainer>
            <InputSearchWithSidebar<CountriesResponse>
              name='country_search'
              inputValue={
                country ? getCountryLocalizedTitle(country, true, language) : ''
              }
              defaultOption={country}
              sidebarTitle={t('country')}
              sidebarInputPlaceholder={t('start_typing_country_name')}
              inputIcon={ArrowIcon}
              displayAllOptionsWithEmptyFilter
              isOptionSelected={(option, selected) =>
                option?.alias === selected?.alias
              }
              getSearchOptions={async (filter) =>
                (countryItems ?? [])
                  .map((i) => i.value)
                  .filter((c) =>
                    getCountryLocalizedTitle(c, true, language)
                      .toLowerCase()
                      .includes(filter?.toLowerCase() ?? '')
                  )
              }
              getKeyForSearchOption={(c) => c.alias}
              getDisplayValueForSearchOption={(c) =>
                getCountryLocalizedTitle(c, true, language)
              }
              onSearchOptionSelected={(selectedCountry) => {
                handleCountryChange({
                  value: selectedCountry,
                  label: getCountryLocalizedTitle(selectedCountry),
                });
              }}
              mainInputValidationPredicate={(v) => !!v}
              errorMessage='this_field_cannot_be_empty'
            />
          </ParcelDetailsSearchInputsContainer>
        </ServicesSubSectionContainer>

        <ServicesSubSectionContainer>
          <ServicesSubSectionHeader>{t('language')}</ServicesSubSectionHeader>

          <ParcelDetailsSearchInputsContainer>
            <InputSearchWithSidebar<string>
              name='language_search'
              inputValue={language?.toUpperCase() ?? ''}
              defaultOption={language ?? null}
              sidebarTitle={t('language')}
              sidebarInputPlaceholder=''
              inputIcon={ArrowIcon}
              displayAllOptionsWithEmptyFilter
              isOptionSelected={(option, selected) => option === selected}
              getSearchOptions={async (filter) =>
                (languageItems ?? [])
                  .map((i) => i.value)
                  .filter((code) =>
                    code.toLowerCase().includes(filter?.toLowerCase() ?? '')
                  )
              }
              getKeyForSearchOption={(code) => code}
              getDisplayValueForSearchOption={(code) => {
                const item = languageItems.find((i) => i.value === code);
                return item ? `${item.label}` : code.toUpperCase();
              }}
              onSearchOptionSelected={(selectedLang) => {
                setLanguage(selectedLang);
              }}
              mainInputValidationPredicate={(v) => !!v}
              errorMessage='this_field_cannot_be_empty'
            />
          </ParcelDetailsSearchInputsContainer>
        </ServicesSubSectionContainer>

        <ServicesSubSectionContainer>
          <ServicesSubSectionHeader>{t('currency')}</ServicesSubSectionHeader>

          <ParcelDetailsSearchInputsContainer>
            <InputSearchWithSidebar<string>
              name='currency_search'
              inputValue={currency ?? ''}
              defaultOption={currency ?? null}
              sidebarTitle={t('currency')}
              sidebarInputPlaceholder=''
              inputIcon={ArrowIcon}
              displayAllOptionsWithEmptyFilter
              isOptionSelected={(option, selected) => option === selected}
              getSearchOptions={async (filter) =>
                (currencyItems ?? [])
                  .map((i) => i.value)
                  .filter((cur) =>
                    cur.toLowerCase().includes(filter?.toLowerCase() ?? '')
                  )
              }
              getKeyForSearchOption={(cur) => cur}
              getDisplayValueForSearchOption={(cur) => {
                const item = currencyItems.find((i) => i.value === cur);
                return item ? item.label : cur;
              }}
              onSearchOptionSelected={(selectedCur) => {
                setCurrency(selectedCur);
              }}
              mainInputValidationPredicate={(v) => !!v}
              errorMessage='this_field_cannot_be_empty'
              disabled={isGuest}
            />
          </ParcelDetailsSearchInputsContainer>
        </ServicesSubSectionContainer>

        <ServicesSubSectionContainer>
          <ServicesSubSectionHeader>{t('measures')}</ServicesSubSectionHeader>

          <ParcelDetailsSearchInputsContainer>
            <FlexContainer
              $gap='1rem'
              $width='100%'>
              <Button
                appearance={
                  measures === Measure.KG_CM
                    ? 'secondary'
                    : 'secondary-without-outline'
                }
                onClick={() => setMeasures(Measure.KG_CM)}
                disabled={isGuest}>
                {t('kg_/_cm')}
              </Button>

              <Button
                appearance={
                  measures === Measure.LB_IN
                    ? 'secondary'
                    : 'secondary-without-outline'
                }
                onClick={() => setMeasures(Measure.LB_IN)}
                disabled={isGuest}>
                {t('lb_/_in')}
              </Button>
            </FlexContainer>
          </ParcelDetailsSearchInputsContainer>
        </ServicesSubSectionContainer>

        <Button
          isLoading={isSaving}
          onClick={async () => {
            await handleSave();
            toggleSidebarByName(SIDEBAR_NAME);
          }}>
          {t('save')}
        </Button>
      </FlexContainer>
    </BottomSidebar>
  );
};

export default observer(HeaderSettingsPopupMobile);
